import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CancelIcon from '@mui/icons-material/Cancel';
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import EditIcon from '@mui/icons-material/Edit';
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SellIcon from '@mui/icons-material/Sell';
import Typography from "@mui/material/Typography";

import ModalElement from "../Modal";
import { fetchProducts, addProduct, editProduct } from "../../actions/productActions";
// import { fetchSurveys } from "../../actions/surveyActions";
import { getCostReport } from "../../actions/costActions";
import { fetchValueModels } from "../../actions/valueActions";
import ProductCreateForm from "../product/ProductCreateForm";

import firstCharToUpper from "../helpers/stringFormat";
import JourneyStepper from "./JourneyStepper";
import history from "../../history";

const Journey = () => {
    const formikRef = useRef(null);
    const dispatch = useDispatch();

    const [productCreateOpen, setProductCreateOpen] = useState(false);
    const [editProductId, setEditProductId] = useState(null);
    const [selectedProductIndex, setSelectedProductIndex] = useState(0);

    const editProductSet = (id) => {
        setEditProductId(id)
        setProductCreateOpen(true)
    }

    const toggleProductCreateOpen = () => {
        setProductCreateOpen(!productCreateOpen)
    }

    const handleClose = () => {
        setProductCreateOpen(false)
        setEditProductId(null)
    }

    const filterSurveysByProductId = (surveys, id) => {
        return Object.fromEntries(
            Object.entries(surveys).filter(
                ([key, survey]) => survey.product.id === id
            )
        );
    };

    useEffect(() => {
        dispatch(fetchProducts());
        // dispatch(fetchSurveys());
        dispatch(getCostReport());
        dispatch(fetchValueModels());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Access state
    const products = useSelector(state => state.product.products)
    const valueModels = useSelector(state => state.value.valueModels)
    const surveys = useSelector(state => state.survey.surveys)
    const currency = useSelector(state => state.profile.dataSettings.currency)

    const selectedProduct = Object.values(products)[selectedProductIndex]

    const productOnSubmit = (values) => {
        // Clone the values object to avoid direct mutation
        const clonedValues = { ...values };
        clonedValues.price_alternative = values.price_alternative.toString().replace(/,/g, "");
        clonedValues.annual_purchase = values.annual_purchase.toString().replace(/,/g, "");

        if (editProductId) {
            dispatch(editProduct(editProductId, clonedValues))
            setEditProductId(null)
        }
        else {
            dispatch(addProduct(clonedValues))
        }
        handleClose()
        if (formikRef.current) {
            formikRef.current.resetForm();
        }

    }

    const handleProductReset = () => {
        setProductCreateOpen(false)
        setEditProductId(null)

        // Access the formikRef current value and call resetForm
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
    };

    function renderProductObject(product, index) {
        if (!product) {
            return null
        }
        return (
            <Card sx={{ width: 280, height: 130, mt: 4, position: 'relative', borderRadius: '16px' }}>
                <CardActionArea
                    onClick={() => setSelectedProductIndex(index)}
                    data-active={selectedProductIndex === index ? '' : undefined}
                    sx={{
                        height: '100%',
                        '&[data-active]': {
                            backgroundColor: 'rgba(144, 238, 144, 0.3)',
                            '&:hover': {
                                backgroundColor: 'rgba(144, 238, 144, 0.5)',
                            },
                        },
                    }}
                >
                    <CardContent>
                        <Grid
                            container
                            sx={{
                                height: '100%', // Ensure the grid takes the full height of the card
                                display: 'flex',
                                flexDirection: 'column', // Stack the grid items vertically
                                justifyContent: 'center', // Center content vertically
                                alignItems: 'flex-start', // Align content to the left horizontally
                            }}
                        >
                            <Grid item>
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                    <SellIcon />
                                    <Typography variant="h6">{product.name}</Typography>
                                </Stack>
                            </Grid>
                            <Grid item>
                                <Typography variant="caption">
                                    {product.revenue_model === 'transactional' ?
                                        `${firstCharToUpper(product.revenue_model)} revenue model with a price per ${product.unit}`
                                        : `Subscription based revenue model with a ${product.frequency} price${product.unit.length > 0 ? ' per ' + product.unit : ''}`}
                                </Typography>
                            </Grid>
                        </Grid>
                    </CardContent>
                </CardActionArea>
            </Card>

        );
    }


    const StitchedPaper = () => {
        return (
            <Card
                elevation={0}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',  // Vertically center the content
                    // borderRadius: 1,
                    border: '1px dashed #d3d3d3',
                    width: 280,
                    height: 130,  // Set the height to allow space for vertical centering
                    // ml: 4,
                    mt: 4,
                    borderRadius: '16px'
                }}
            >
                <CardContent>
                    <Stack direction="row" gap={1} alignItems="center" sx={{ height: '100%' }}>
                        <Box>
                            <IconButton
                                color="primary"
                                onClick={toggleProductCreateOpen}
                                sx={{
                                    padding: 1, // Adjust padding as needed
                                    borderRadius: '8px', // Rounded corners instead of circular
                                    border: '1px solid #ccc', // Solid border instead of dashed
                                    backgroundColor: 'transparent', // Transparent background
                                    display: 'flex',
                                    alignItems: 'center', // Align the icon in the center
                                    justifyContent: 'center', // Center the icon horizontally
                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)', // Subtle elevation effect (default shadow)
                                }}
                            >
                                <AddRoundedIcon />
                            </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Add Product
                            </Typography>
                            <Typography variant="caption">
                                A product is the starting point of the value creation journey
                            </Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

        );
    };

    const productFormActions = (loading) => {
        return (
            <React.Fragment>
                {editProductId ? null
                    : <Box>
                        <Button type="submit" variant="contained" disabled={loading} sx={{ flexGrow: 1, textTransform: 'none' }}>Create</Button>
                    </Box>
                }
                {editProductId ?
                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" startIcon={<EditIcon />} size="small" type="submit" disabled={loading} sx={{ textTransform: 'none' }}>Save</Button>
                        <Button variant="outlined" startIcon={<CancelIcon />} size="small" onClick={handleProductReset} disabled={loading} sx={{ textTransform: 'none' }}>Cancel</Button>
                    </Stack>
                    : null
                }
            </React.Fragment>
        )
    }

    function renderCreateProductModal() {
        return (
            <ModalElement
                modalOpen={productCreateOpen}
                modalWidth={400}
                handleClose={handleClose}
                header={`${editProductId ? 'Edit' : 'Create'} Product`}
                instruction="Please be as detailed as possible"
                content={
                    <ProductCreateForm
                        currency={currency}
                        editProductId={editProductId}
                        editProduct={products[editProductId]}
                        formActions={productFormActions}
                        onSubmit={productOnSubmit}
                    />
                }
            />
        )
    }

    const renderProductAction = (id) => {
        return (
            <Button variant="contained" onClick={() => editProductSet(id)} sx={{ mr: '30%', mt: 3, textTransform: 'none' }}>
                Edit Product
            </Button>
        )
    }

    const renderValueAction = (id) => {
        return (
            <Button variant="contained" onClick={() => history.push(`/value-models?id=${id}`)} sx={{ mr: '30%', mt: 3, textTransform: 'none' }}>
                Go to Value Models
            </Button>
        )
    }

    const renderSurveyAction = (id) => {
        return (
            <Button variant="contained" onClick={() => history.push(`/surveys?id=${id}`)} sx={{ mr: '30%', mt: 3, textTransform: 'none' }}>
                Go to Surveys
            </Button>
        )
    }

    const renderCostAction = (id) => {
        return (
            <Button variant="contained" onClick={() => history.push('/costs')} sx={{ mr: '30%', mt: 3, textTransform: 'none' }}>
                Go to Costs
            </Button>
        )
    }

    const renderPresentationAction = (id) => {
        return (
            <Button variant="contained" onClick={() => history.push(`/presentation?id=${id}`)} sx={{ mr: '30%', mt: 3, textTransform: 'none' }}>
                Go to Presentations
            </Button>
        )
    }

    return (
        <Box sx={{ ml: 4 }}>
            {products && Object.keys(products).length > 0 ?
                <Grid container gap={2}>
                    {Object.values(products).map((product, index) => (
                        <Grid key={index} item sx={{ width: 280 }}>
                            {renderProductObject(product, index)}
                            {index === selectedProductIndex &&
                                <JourneyStepper
                                    product={selectedProduct}
                                    productAction={() => renderProductAction(product.id)}
                                    valueModels={valueModels.filter(model => model.product.id === product.id)}
                                    valueAction={() => renderValueAction(product.id)}
                                    surveys={filterSurveysByProductId(surveys, product.id)}
                                    surveyAction={() => renderSurveyAction(product.id)}
                                    costAction={() => renderCostAction(product.id)}
                                    presentationAction={() => renderPresentationAction(product.id)}
                                />
                            }
                        </Grid>
                    ))}
                    <Grid key="addProduct" item>
                        <StitchedPaper />
                    </Grid>
                </Grid>
                :
                <Grid container sx={{ maxWidth: 1050 }}>
                    <Grid key="addProduct" item>
                        <StitchedPaper />
                    </Grid>
                </Grid>
            }
            {renderCreateProductModal()}
        </Box>
    )

}

export default Journey